/**
 * Content Hook
 * Fetches content from Shopify metafields with fallback to defaults
 *
 * To set up metafields in Shopify Admin:
 * 1. Go to Settings → Custom data → Metafields
 * 2. Add definitions for Shop or Pages
 * 3. Use namespace "custom" with the keys defined below
 *
 * Shop Metafields (Settings → Custom data → Shop):
 * - custom.phone: Single line text
 * - custom.email: Single line text
 * - custom.address: Multi-line text
 * - custom.business_hours: JSON
 * - custom.social_links: JSON
 * - custom.announcement_text: Single line text
 * - custom.free_shipping_threshold: Number (integer)
 *
 * Page Metafields (Settings → Custom data → Pages):
 * - custom.hero_title: Single line text
 * - custom.hero_subtitle: Multi-line text
 * - custom.hero_badge: Single line text
 * - custom.hero_image: File (image)
 * - custom.sections: JSON (array of section objects)
 * - custom.stats: JSON (array of stat objects)
 * - custom.faqs: JSON (array of FAQ objects)
 * - custom.cta_title: Single line text
 * - custom.cta_subtitle: Single line text
 * - custom.cta_button_text: Single line text
 * - custom.cta_button_link: URL
 */

import { useState, useEffect } from 'react';
import {
  getShopMetafields,
  getPageByHandle,
  parseMetafields,
  getMetafieldValue,
  type ShopifyPage,
} from '~/lib/shopify/api';
import {
  siteConfig,
  aboutContent,
  supportContent,
  contactContent,
  faqContent,
  shippingContent,
  warrantyContent,
  returnsContent,
} from './content';

// ===========================================
// Site Config Hook
// ===========================================

export interface SiteContent {
  name: string;
  tagline: string;
  description: string;
  contact: {
    phone: string;
    email: string;
    address: string;
  };
  social: {
    linkedin?: string;
    twitter?: string;
    facebook?: string;
    youtube?: string;
  };
  businessHours: {
    weekdays: string;
    weekends: string;
    timezone: string;
  };
  announcement?: string;
  freeShippingThreshold?: number;
}

export function useSiteContent() {
  const [content, setContent] = useState<SiteContent>(siteConfig);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    getShopMetafields()
      .then((shop) => {
        if (cancelled || !shop) return;

        const metafields = parseMetafields(shop.metafields);

        setContent({
          name: shop.name || siteConfig.name,
          tagline: shop.brand?.slogan || siteConfig.tagline,
          description: shop.brand?.shortDescription || shop.description || siteConfig.description,
          contact: {
            phone: getMetafieldValue(metafields, 'phone', siteConfig.contact.phone),
            email: getMetafieldValue(metafields, 'email', siteConfig.contact.email),
            address: getMetafieldValue(metafields, 'address', siteConfig.contact.address),
          },
          social: getMetafieldValue(metafields, 'social_links', siteConfig.social),
          businessHours: getMetafieldValue(metafields, 'business_hours', siteConfig.businessHours),
          announcement: getMetafieldValue(metafields, 'announcement_text', undefined),
          freeShippingThreshold: getMetafieldValue(metafields, 'free_shipping_threshold', 50),
        });
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  return { content, loading };
}

// ===========================================
// Page Content Hook
// ===========================================

export interface PageContent {
  title: string;
  body?: string;
  hero?: {
    title?: string;
    subtitle?: string;
    badge?: string;
    image?: string;
  };
  sections?: unknown[];
  stats?: Array<{ value: string; label: string; description?: string }>;
  faqs?: Array<{ category?: string; questions: Array<{ q: string; a: string }> }>;
  cta?: {
    title?: string;
    subtitle?: string;
    buttonText?: string;
    buttonLink?: string;
  };
}

const defaultPageContent: Record<string, PageContent> = {
  about: {
    title: aboutContent.hero.title,
    hero: aboutContent.hero,
    stats: aboutContent.stats,
    cta: {
      title: aboutContent.cta.title,
      subtitle: aboutContent.cta.subtitle,
      buttonText: aboutContent.cta.primaryButton.text,
      buttonLink: aboutContent.cta.primaryButton.href,
    },
  },
  support: {
    title: supportContent.hero.title,
    hero: {
      title: supportContent.hero.title,
      subtitle: supportContent.hero.subtitle,
    },
    faqs: supportContent.faqs,
  },
  contact: {
    title: contactContent.hero.title,
    hero: {
      title: contactContent.hero.title,
      subtitle: contactContent.hero.subtitle,
    },
  },
  faq: {
    title: faqContent.hero.title,
    hero: {
      title: faqContent.hero.title,
      subtitle: faqContent.hero.subtitle,
    },
    faqs: faqContent.categories.map((cat) => ({
      category: cat.name,
      questions: cat.questions,
    })),
  },
  shipping: {
    title: shippingContent.hero.title,
    hero: shippingContent.hero,
  },
  warranty: {
    title: warrantyContent.hero.title,
    hero: warrantyContent.hero,
  },
  returns: {
    title: returnsContent.hero.title,
    hero: returnsContent.hero,
  },
};

export function usePageContent(slug: string) {
  const [page, setPage] = useState<ShopifyPage | null>(null);
  const [content, setContent] = useState<PageContent>(
    defaultPageContent[slug] || { title: 'Page' }
  );
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);

    getPageByHandle(slug)
      .then((shopifyPage) => {
        if (cancelled) return;

        if (shopifyPage) {
          setPage(shopifyPage);

          const metafields = parseMetafields(shopifyPage.metafields);
          const defaults = defaultPageContent[slug] || { title: shopifyPage.title };

          setContent({
            title: shopifyPage.title || defaults.title,
            body: shopifyPage.body || undefined,
            hero: {
              title: getMetafieldValue(metafields, 'hero_title', defaults.hero?.title),
              subtitle: getMetafieldValue(metafields, 'hero_subtitle', defaults.hero?.subtitle),
              badge: getMetafieldValue(metafields, 'hero_badge', defaults.hero?.badge),
              image: getMetafieldValue(metafields, 'hero_image', defaults.hero?.image),
            },
            sections: getMetafieldValue(metafields, 'sections', defaults.sections),
            stats: getMetafieldValue(metafields, 'stats', defaults.stats),
            faqs: getMetafieldValue(metafields, 'faqs', defaults.faqs),
            cta: {
              title: getMetafieldValue(metafields, 'cta_title', defaults.cta?.title),
              subtitle: getMetafieldValue(metafields, 'cta_subtitle', defaults.cta?.subtitle),
              buttonText: getMetafieldValue(metafields, 'cta_button_text', defaults.cta?.buttonText),
              buttonLink: getMetafieldValue(metafields, 'cta_button_link', defaults.cta?.buttonLink),
            },
          });
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [slug]);

  return { page, content, loading };
}

export default {
  useSiteContent,
  usePageContent,
};
